import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  getScheduledMessage,
  cancelScheduledMessage,
  deleteScheduledMessage,
  resendScheduledMessage,
  retryScheduledMessage,
  forceDispatchScheduledMessage,
  sendScheduledMessageTemplate,
} from '../../../api/scheduledMessages';
import {
  getScheduledMessageAdmin,
  cancelScheduledMessageAdmin,
  deleteScheduledMessageAdmin,
  resendScheduledMessageAdmin,
  retryScheduledMessageAdmin,
  forceDispatchScheduledMessageAdmin,
  sendScheduledMessageTemplateAdmin,
} from '../../../api/scheduledMessagesAdmin';
import { getContact } from '../../../api/contacts';
import { getApiErrorMessage } from '../../../utils/errors';
import ScheduledMessageAdminDetail from './ScheduledMessageAdminDetail';
import ScheduledMessageUserDetail from './ScheduledMessageUserDetail';
import {
  buildListQueryString,
  buildScheduledMessagesPath,
  getScheduledMessageDownloadableAttachments,
} from '../utils';

function ScheduledMessageShowPage({ mode }) {
  const { scheduledMessageId } = useParams();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agentId') || '';
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [resending, setResending] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [retryError, setRetryError] = useState('');
  const [resendError, setResendError] = useState('');
  const [dispatchError, setDispatchError] = useState('');
  const [sendTemplateError, setSendTemplateError] = useState('');

  const listPath = `${buildScheduledMessagesPath(mode)}${buildListQueryString(agentId, '')}`;
  const downloadableAttachments = useMemo(
    () => getScheduledMessageDownloadableAttachments(item),
    [item]
  );

  const loadItem = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data =
        mode === 'admin'
          ? await getScheduledMessageAdmin(scheduledMessageId)
          : await getScheduledMessage(scheduledMessageId);

      if (mode !== 'admin' && data.contact_id) {
        try {
          const contact = await getContact(data.contact_id);
          data.contact = { id: contact.id, external_id: contact.external_id };
        } catch {
          // Contact may be deleted or inaccessible.
        }
      }

      setItem(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o agendamento.'));
      setItem(null);
    } finally {
      setLoading(false);
    }
  }, [mode, scheduledMessageId]);

  useEffect(() => {
    loadItem();
  }, [loadItem]);

  const clearActionErrors = () => {
    setCancelError('');
    setDeleteError('');
    setRetryError('');
    setResendError('');
    setDispatchError('');
    setSendTemplateError('');
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancelar este agendamento?')) {
      return;
    }

    clearActionErrors();
    setCancelling(true);
    try {
      if (mode === 'admin') {
        await cancelScheduledMessageAdmin(scheduledMessageId);
      } else {
        await cancelScheduledMessage(scheduledMessageId);
      }
      await loadItem();
    } catch (err) {
      setCancelError(getApiErrorMessage(err, 'Não foi possível cancelar o agendamento.'));
    } finally {
      setCancelling(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir este agendamento permanentemente?')) {
      return;
    }

    clearActionErrors();
    setDeleting(true);
    try {
      if (mode === 'admin') {
        await deleteScheduledMessageAdmin(scheduledMessageId);
      } else {
        await deleteScheduledMessage(scheduledMessageId);
      }
      navigate(listPath);
    } catch (err) {
      setDeleteError(getApiErrorMessage(err, 'Não foi possível excluir o agendamento.'));
    } finally {
      setDeleting(false);
    }
  };

  const handleRetry = async () => {
    if (!window.confirm('Este agendamento está preso em processamento. Reenfileirar envio?')) {
      return;
    }

    clearActionErrors();
    setRetrying(true);
    try {
      if (mode === 'admin') {
        await retryScheduledMessageAdmin(scheduledMessageId);
      } else {
        await retryScheduledMessage(scheduledMessageId);
      }
      await loadItem();
    } catch (err) {
      setRetryError(getApiErrorMessage(err, 'Não foi possível reprocessar o agendamento.'));
    } finally {
      setRetrying(false);
    }
  };

  const handleResend = async () => {
    if (!window.confirm('Reenviar a mensagem gerada para o contato?')) {
      return;
    }

    clearActionErrors();
    setResending(true);
    try {
      if (mode === 'admin') {
        await resendScheduledMessageAdmin(scheduledMessageId);
      } else {
        await resendScheduledMessage(scheduledMessageId);
      }
      await loadItem();
    } catch (err) {
      setResendError(getApiErrorMessage(err, 'Não foi possível reenviar a mensagem.'));
    } finally {
      setResending(false);
    }
  };

  const handleForceDispatch = async () => {
    if (
      !window.confirm(
        'Gerar e enviar a mensagem agora? O horário agendado será ignorado. Isso pode levar alguns minutos.'
      )
    ) {
      return;
    }

    clearActionErrors();
    setDispatching(true);
    try {
      if (mode === 'admin') {
        await forceDispatchScheduledMessageAdmin(scheduledMessageId);
      } else {
        await forceDispatchScheduledMessage(scheduledMessageId);
      }
      await loadItem();
    } catch (err) {
      setDispatchError(getApiErrorMessage(err, 'Não foi possível enviar o agendamento agora.'));
    } finally {
      setDispatching(false);
    }
  };

  const handleSendTemplate = async () => {
    if (
      !window.confirm(
        'Enviar apenas o template WhatsApp? O conteúdo completo será entregue após o contato interagir.'
      )
    ) {
      return;
    }

    clearActionErrors();
    setSendingTemplate(true);
    try {
      if (mode === 'admin') {
        await sendScheduledMessageTemplateAdmin(scheduledMessageId);
      } else {
        await sendScheduledMessageTemplate(scheduledMessageId);
      }
      await loadItem();
    } catch (err) {
      setSendTemplateError(getApiErrorMessage(err, 'Não foi possível enviar o template.'));
    } finally {
      setSendingTemplate(false);
    }
  };

  if (loading) {
    return (
      <div className="surface-card px-4 py-12 text-center">
        <p className="text-content-muted text-sm">Carregando agendamento...</p>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="space-y-4">
        <Link to={listPath} className="text-sm text-forge-700 hover:text-forge-800 font-medium">
          ← Voltar aos agendamentos
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error || 'Agendamento não encontrado.'}</p>
        </div>
      </div>
    );
  }

  const detailProps = {
    item,
    mode,
    agentId,
    listPath,
    downloadableAttachments,
    cancelError,
    deleteError,
    retryError,
    resendError,
    dispatchError,
    sendTemplateError,
    cancelling,
    deleting,
    retrying,
    resending,
    dispatching,
    sendingTemplate,
    onCancel: handleCancel,
    onDelete: handleDelete,
    onRetry: handleRetry,
    onResend: handleResend,
    onForceDispatch: handleForceDispatch,
    onSendTemplate: handleSendTemplate,
    onBack: () => navigate(listPath),
  };

  if (mode === 'admin') {
    return <ScheduledMessageAdminDetail {...detailProps} />;
  }

  return <ScheduledMessageUserDetail {...detailProps} />;
}

export default ScheduledMessageShowPage;
